<template>
    <v-layout justify-center class="pa-4">
        <v-responsive width="100%" height="100%" max-width="800">
            <list-heading title="나누리상품권 주문 통계" />

            <v-row class="ma-n2 mt-n1">
                <v-col class="pa-2" cols="auto">
                    <list-summary v-bind="{ summary }" />
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <nshop-statistics-mode />
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <nshop-order-list-pg />
                </v-col>
                <v-col v-if="showsViewer" class="pa-2" cols="auto">
                    <nshop-list-viewer />
                </v-col>
                <v-spacer />
                <v-col class="pa-2" cols="auto">
                    <list-excel v-slot="{ attrs, on }" v-on="{ excel }">
                        <v-card outlined style="overflow: hidden;">
                            <v-btn height="40" color="white" elevation="0" class="text-caption green--text" style="margin:-1px" v-bind="{ ...attrs, loading }" v-on="on">
                                <v-icon> mdi-microsoft-excel </v-icon>
                                <span> 엑셀다운로드 </span>
                            </v-btn>
                        </v-card>
                    </list-excel>
                </v-col>
                <v-col class="pa-2" cols="12">
                    <v-data-table v-bind="{ loading, headers, items }" disable-sort disable-pagination hide-default-footer class="v-sheet--outlined" :items-per-page="-1" ref="data-table" mobile-breakpoint="880">
                        <template v-for="(header, index) in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }"> <span :key="index" :title="header.withTitle ? header.formatter.bind(item)(value, item) : undefined" v-html="header.formatter.bind(item)(value, item)" /> </template>

                        <template #footer>
                            <v-divider />
                            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-2" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import { attrs_switch } from "@/assets/variables";
import { initDataTableHeaders } from "@/assets/lib";
import { NSHOP_STATISTICS_MODES } from "@/store/nshop";

import ListExcel from "@/components/console/dumb/list/list-excel.vue";
import ListHeading from "@/components/console/dumb/list/list-heading.vue";
import ListSummary from "@/components/console/dumb/list/list-summary.vue";
import NshopListViewer from "@/components/console/nshop/nshop-list-viewer.vue";
import NshopOrderListPg from "@/components/console/nshop/orders/nshop-order-list-pg.vue";
import NshopStatisticsMode from "@/components/console/nshop/statistics/nshop-statistics-mode.vue";

const mode_default = NSHOP_STATISTICS_MODES.DAILY.value;

const headers = initDataTableHeaders([
    {
        text: "#",
        value: "index",
        width: 80,
        align: "end",
    },
    {
        text: "집계기준",
        value: "title",
        width: 228,
    },
    {
        text: "주문건수",
        value: "order.count",
        width: 110,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "매출금액",
        value: "order.sales",
        width: 160,
        align: "end",
        formatterType: "number",
        formatterSuffix: "원",
    },
    {
        text: "알림톡\n발송건수",
        value: "message.kakao.count",
        width: 110,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "문자\n발송건수",
        value: "message.plain.count",
        width: 110,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
]);

export default {
    components: {
        ListExcel,
        ListHeading,
        ListSummary,
        NshopListViewer,
        NshopOrderListPg,
        NshopStatisticsMode,
    },
    data: () => ({
        statistics: [],

        limit: 20,
        summary: { totalCount: 0 },

        headers,
        attrs_switch,

        loading: false,
        showsSearch: true,
    }),
    computed: {
        // 뷰어 콘솔은 애초에 자기 실적만 집계되므로(서버에서 강제) 추천인 필터가 의미가 없다.
        showsViewer() {
            return !this.$hasScope(["viewer"]);
        },
        items() {
            return this.statistics.map((item, index) => ({
                ...item,
                index: this.summary.totalCount - index - this.skip,
            }));
        },
        mode() {
            return this.$route.query.mode || mode_default;
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;
            delete query.mode;

            return { ...query };
        },
    },
    methods: {
        async init() {
            this.orders = [];
            this.summary = { totalCount: 0 };
            this.loading = false;

            await this.search();
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { mode, skip, limit, params } = this;
                let { summary, statistics } = await api.console.nshop.statistics.gets({
                    headers: { mode, skip, limit },
                    params,
                });

                this.summary = summary;
                this.statistics = statistics;
            } finally {
                this.loading = false;
            }
        },

        async excel({ skip = 0, limit = 0 } = {}) {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { mode, params } = this;
                let { statistics } = await api.console.nshop.statistics.gets({
                    headers: { mode, skip, limit },
                    params,
                });

                let { headers } = this;
                let fileName = "나누리상품권_주문통계";
                fileName += `(${NSHOP_STATISTICS_MODES[this.mode]?.text})`;
                this.$excel(statistics, headers, fileName);
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        params() {
            this.search();
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .line-height-1-3 {
        line-height: 1.3;
    }
    .line-height-1-5 {
        line-height: 1.5;
    }
    .max-width-0 {
        max-width: 0;
    }
    .word-break-keep-all {
        word-break: keep-all;
    }
    .white-space-pre-line {
        white-space: pre-line;
    }
    .cursor-pointer {
        cursor: pointer;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
    .v-small-dialog__activator {
        height: 100%;
    }
    .v-small-dialog__activator__content {
        width: 100%;
    }

    th:first-of-type:not(:last-of-type),
    td:first-of-type:not(:last-of-type) {
        border-right: thin solid rgba(0, 0, 0, 0.12);
    }

    .v-data-table__expanded__content {
        box-shadow: none !important;
    }
    .vertical-align-top {
        vertical-align: top !important;
    }
    .vertical-align-middle {
        vertical-align: middle !important;
    }
}
</style>
